<template>
  <div class="dd-model-viewer-container">
    <div
      ref="container"
      class="three-container"
    />
    <div
      v-if="isLoading"
      class="loading-overlay"
    >
      <div class="loading-spinner" />
      <p>正在初始化社区数字孪生空间 - {{ currentLoadingPart || '同步地理信息' }}</p>
      <div class="progress-bar">
        <div
          class="progress-fill"
          :style="{ width: loadingProgress + '%' }"
        />
      </div>
      <p class="progress-text">
        {{ loadingProgress }}%
      </p>
    </div>
    <div class="control-panel">
      <button
        class="control-btn"
        @click="resetView"
      >
        重置视角
      </button>
      <button
        class="control-btn"
        @click="toggleRotation"
      >
        {{ isRotating ? '停止旋转' : '开始旋转' }}
      </button>
    </div>
  </div>
</template>

<script setup>
// 导入 watch
import { ref, onMounted, onUnmounted } from 'vue'

// 定义事件
const emit = defineEmits(['object-click', 'buildings-update'])
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
// import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { KTX2Loader }   from 'three/examples/jsm/loaders/KTX2Loader.js'
// 下面两个路径随 three 版本略有差异，用其一能成功即可：
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
// 或：import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js'

// 引入外部效果模块人云车
import { createClouds } from '@/scene/effects/clouds'
import { createCrowd }  from '@/scene/effects/crowd'
import { createTraffic } from '@/scene/effects/traffic'




//用于楼宇气泡
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js'

/////////////关于接口的数据的对接///////////////////////////////////////////////////////
// 2) 引入映射表
import buildingNameMap from '@/config/building-map'

import axios from 'axios'  // ← 在顶部加这个，用于接口调用

// 响应式变量：加载状态，云。人，车的控制器
let cloudsCtl = null
let crowdCtl  = null
let trafficCtl = null
const clock = new THREE.Clock() // 用于 delta / elapsed
// 顶部同其它变量一起：
let isDestroyed = false


// —— 新增：保存接口数据（可选）
const buildingList = ref([])

const devicePage2 = ref([])
///////////////////////////////居民信息对接建模/////////////////////////////////////////////////////////////
// —— 新增：楼栋接口的索引（便于按 小区名+楼号 或者 按 楼牌中文名 取一条）
const buildingsApiByKey = new Map()        // key: `${小区名}__${楼号}` → 楼栋接口对象
const buildingsApiByMapName = new Map()    // key: `Community_Map_Name`（如 “佳园17#”）→ 楼栋接口对象

// —— 规范化 + 组 key 的小工具，.trim() 去掉字符串两端的空白字符。
//.replace(/\s+/g, '') 将字符串中的所有空白字符（包括空格、制表符等）替换为空字符串。
function normalizeCN (s='') { return String(s).trim().replace(/\s+/g, '') }
// 工具函数区，替换现有的 normalizeNo
function normalizeNo(s = '') {
  const str = String(s).trim();
  // 优先抓结尾或 # 前的一段数字；否则取最后一段数字
  const m = str.match(/(\d+)(?=(?:\s*#)?\s*$)/) || (str.match(/\d+/g) || []).slice(-1);
  const num = Array.isArray(m) ? m[0] : m;
  return num ? String(parseInt(num, 10)) : '';
}

function makeKey (community, buildingNo) {
  return `${normalizeCN(community)}__${normalizeNo(buildingNo)}`
}

// 解析模型楼名（如“佳园23# / 善园7号楼 / 臻园001#”）→ { prefix(中文前缀), no(数字) }
function parseModelLabel(label = '') {
  const s = normalizeLabelStrict(label); // 去空格、全角#转半角
  const m = s.match(/^(.+?)(\d+)\s*#?$/);
  if (!m) return null;
  return {
    prefix: normalizeCN(m[1]),   // '佳园' / '善园' / '和园' / '臻园'...
    no:     normalizeNo(m[2])    // '23' / '7' / '001' -> '1'
  };
}

// 安全 stringify（防循环引用），方便一次性把原始返回打全量日志
function safeStringify(obj) {
  const seen = new WeakSet();
  return JSON.stringify(obj, (k, v) => {
    if (typeof v === 'object' && v !== null) {
      if (seen.has(v)) return '[Circular]';
      seen.add(v);
    }
    return v;
  }, 2);
}




// —— 居民行匹配规则（你可以随时改严/放松）
const RESIDENT_MATCH = {
  community: 'loose', // 'strict' | 'loose'  小区名：默认宽松（包含即可）
  number:    'strict' // 'strict'            楼号：必须严格相等
};

// —— 前端二次过滤：这一条居民是否属于“目标小区 + 目标楼号”
function residentRowMatches(row, target) {
  const wantNo = normalizeNo(target.buildingNo);
  const gotNo  = normalizeNo(row.buildingNumber);
  if (gotNo !== wantNo) return false; // 楼号必须相等

  const wantComm = normalizeCN(target.communityName);
  const gotComm  = normalizeCN(row.communityName);

  if (RESIDENT_MATCH.community === 'strict') {
    return gotComm === wantComm;
  } else {
    // 宽松：名称包含即可（处理“某园A区/某园”之类口径差异）
    if (!wantComm || !gotComm) return true;
    return gotComm.includes(wantComm) || wantComm.includes(gotComm);
  }
}


// —— 把中文楼牌名统一成：<中文前缀><数字>#
function normalizeLabel (s = '') {
  return String(s)
    .trim()
    .replace(/\s+/g, '')      // 去空格
    .replace(/[＃#]/g, '#')   // 全角/半角 #
    .replace(/(号楼|楼)$/, '#'); // “7号楼 / 7楼” -> “7#”
}
// //说明：以后凡是“中文楼牌名（Community_Map_Name 或 模型里中文名）”的比较，统一用 normalizeLabel。
// normalizeCN 继续用于小区名，normalizeNo 用于楼号。

///////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////楼栋信息对接建模/////////////////////////////////////////////////////////////
// —— 新增：按接口字段 Community_Map_Name 建一个索引（名字 -> 该楼全部信息）
const apiByMapName = new Map()

// 统一名字，确保“臻1# / 臻园1# / 臻1＃”都能匹配到同一条
function normalizeLabelStrict(s = '') {
  return String(s).trim()
    .replace(/\s+/g, '')  // 去掉所有空白
    .replace(/＃/g, '#'); // 全角# -> 半角#
}
// function normalizeNo(s = '') {
//   const str = String(s).trim();
//   const m = str.match(/(\d+)(?=(?:\s*#)?\s*$)/) || (str.match(/\d+/g) || []).slice(-1);
//   const num = Array.isArray(m) ? m[0] : m;
//   return num ? String(parseInt(num, 10)) : '';
// }

function rebuildApiIndex () {
  apiByMapName.clear()// 重建索引,.clear() 清空旧数据
  const list = Array.isArray(buildingList.value) ? buildingList.value : [] //确保接口中的数据是数组
  for (const it of list) {// 遍历每一条楼栋记录
    const k = normalizeLabelStrict(
      it?.Community_Map_Name ?? it?.community_map_name ?? it?.CommunityMapName ?? ''
      // 确保取到 接口中的Community_Map_Name 字段
    )
    if (k) apiByMapName.set(k, it)// 建立索引，k是标准化后的楼牌名，it是对应的楼栋信息对象，也就是其中一个楼的数组元素

  }
  console.log('✅ 楼栋索引（键值对）建立完成：', apiByMapName.size)
}

//////////////////////////////////////////////////////////////////

// —— 新增：统一的 axios 实例，避免长时间卡住挂载
const api = axios.create({
  baseURL: 'https://jqej.zysign.net/api/mini/ys',
  timeout: 80000, // 超时避免阻塞渲染
})


const AREA = {
  id: '2c2c80848d7d601e018d813b05a00101',
  name: '荆泉北路社区'
}

// 通用的分页数据获取函数

// —— 新增：每栋楼的居民缓存：key = `${communityName}__${buildingNumber}` -> rows[]
const residentsCacheByKey = new Map();

// 只查某一栋的居民（自动分页/重试/限速），并缓存
// 只查某一栋的居民（自动分页/重试/限速），并缓存（⚠️加了“前端严格过滤”）
async function loadResidentsFor(communityName, buildingNumber, opts = {}) {
  // ★新增：过滤用的小区名（可与请求时传给后端的 communityName 不同）
  const filterCommunity = normalizeCN(opts.filterCommunityName ?? communityName);

  // ★修改：缓存 key 用“过滤口径”的社区名，避免 '' 造成缓存冲突
  const key = makeKey(filterCommunity, buildingNumber);
  const cached = residentsCacheByKey.get(key);
  if (cached) return cached;

  console.log('[居民API] 请求参数 =', { communityName, buildingNumber, filterCommunity });

  // 直接按“请求参数”去后端拿数据（communityName 可能是前缀，也可能是 ''，由调用方决定）
  const all = await fetchAllPages(
    '/resident/information',
    {
      query_code: 'person_info',
      schema_code: 'person_info',
      area: AREA,
      communityName,     // ← 这是发给后端的查询条件
      buildingNumber,
      unitNumber: '',
      houseNumber: '',
      houseType: '',
      houseOwner: '',
      personName: '',
    },
    {
      pageSizeParam: 'pageSize',
      pageSize: 50,
      throttleMs: 600,
      maxRetries: 5,
      timeout: 20000,
    }
  );

  // 统一字段（✱ 扩展了很多原字段，并把原始行挂到 _raw 方便以后用）
  const mapped = all.map(it => ({
    communityName: it?.communityName ?? '',
    buildingNumber: it?.buildingNumber ?? '',
    unitNumber: it?.unitNumber ?? '',
    houseNumber: it?.houseNumber ?? '',
    personName: it?.personName ?? '',
    phone: it?.phone ?? '',
    idcard: it?.idCard || it?.idcard || '',
    houseOwner: it?.houseOwner ?? '',
    houseType: it?.houseType ?? '',
    emptyOrNot: it?.emptyOrNot ?? '',

    // ✱ 新增的常见字段（按你截图里那些）
    sex: it?.sex ?? '',
    nation: it?.nation ?? '',
    education: it?.education ?? '',
    domicilePlace: it?.domicilePlace ?? '',
    workunit: it?.workunit ?? '',
    politicsStatus: it?.politicsStatus ?? '',
    maritalStatus: it?.maritalStatus ?? '',
    // 部门字段是数组：拼成人类可读的字符串
    deptName: Array.isArray(it?.deptName) ? it.deptName.map(d => d?.name).filter(Boolean).join(' / ') : '',

    remark: it?.remark ?? '',

    // 调试辅助
    buildingNumber_norm: normalizeNo(it?.buildingNumber ?? ''),

    // ✱ 保留原始行（以后要看全部字段/导出就用它）
    _raw: it
  }));


  // ★修改：前端“严格过滤”时，用 filterCommunity（不受后端请求参数影响）
  const target = { communityName: filterCommunity, buildingNo: buildingNumber };
  const rows = mapped.filter(r => residentRowMatches(r, target));

  // === 调试日志：把“命中这一栋”的原始接口记录打印出来（带完整字段） ===
  try {
    // 用与前端过滤一致的口径，在“原始接口行”里再次筛一遍，得到原始命中明细
    const rawMatched = (Array.isArray(all) ? all : []).filter(it => {
      // 只取到过滤所需的两个关键字段进行匹配
      const r = {
        communityName: it?.communityName ?? '',
        buildingNumber: it?.buildingNumber ?? ''
      };
      return residentRowMatches(r, target);
    });

    console.groupCollapsed(
      `%c[居民API] 命中 ${rows.length} 人 (请求 communityName="${communityName}" | 过滤 communityName="${target.communityName}" | 楼号=${target.buildingNo})`,
      'color:#4fc08d'
    );
    console.log('→ 过滤目标 target =', target);
    console.log('→ 接口返回(未过滤)条数 =', Array.isArray(all) ? all.length : 0);
    console.table(rows.slice(0, Math.min(50, rows.length))); // 先给精简可读表（前50条）
    console.log('→ 原始命中明细(完整字段)：\n', safeStringify(rawMatched)); // 再给完整原始数据（可复制）
    console.groupEnd();

    // （可选）若需要把日志上报到服务端，再开下面这段：
    // await api.post('/debug/log', {
    //   type: 'residentMatched',
    //   target,
    //   requestCommunityName: communityName,
    //   matchedCount: rawMatched.length,
    //   rows: rawMatched
    // });
  } catch (e) {
    console.warn('[居民API] 日志输出失败：', e);
  }


  console.log(`[居民API] 未过滤=${mapped.length}，过滤后=${rows.length}，目标=`, target);
  if (rows.length === 0 && mapped.length > 0) {
    console.warn('⚠️ 后端有返回但被过滤掉，检查社区名口径差异或调整 RESIDENT_MATCH.community。', {
      sample: mapped.slice(0, 5)
    });
  }

  residentsCacheByKey.set(key, rows);
  console.log(`👥 已缓存 ${key}：${rows.length} 人`);

  return rows;
}



// 小睡一下（限流/退避用）
const sleep = (ms) => new Promise(res => setTimeout(res, ms));

async function fetchAllPages(url, baseParams = {}, opts = {}) {
  const {
    pageParam = 'page',          // 后端分页字段名
    pageSizeParam = 'pageSize',  // 后端若支持再传；不支持就不要依赖它判断尾页
    pageSize = 50,               // 仅用于“第一次页大小”参考，不作为强停止条件
    maxRetries = 4,              // 单页重试次数 ↑
    retryDelay = 1000,           // 初始退避 ↑
    timeout = 20000,             // 单页超时 ↑
    throttleMs = 250,            // 每页间隔，减少 429
    maxPages = 999               // 保险上限，防止死循环
  } = opts;

  const allData = [];
  let page = 1;
  let effectiveFirstPageLen = null; // 记录首批返回的真实每页大小
  let hasMore = true;

  while (hasMore && page <= maxPages) {
    // throttle：每页之间稍等，防 429
    if (page > 1) await sleep(throttleMs);

    let attempts = 0;
    let resp = null;

    // 针对 429/超时做退避重试（指数退避）
    while (attempts <= maxRetries && !resp) {
      attempts++;
      try {
        const payload = { ...baseParams, [pageParam]: page };
        if (pageSizeParam) payload[pageSizeParam] = pageSize;

        resp = await api.post(url, payload, { timeout });
      } catch (err) {
        const status = err?.response?.status;
        const backoff = retryDelay * Math.pow(2, attempts - 1);
        console.warn(`请求 ${url} 第 ${page} 页失败（尝试 ${attempts}/${maxRetries + 1}，status=${status || 'N/A'}），${backoff}ms 后重试…`);
        if (attempts > maxRetries) {
          console.warn(`放弃第 ${page} 页，停止抓取 ${url}（返回部分数据）。`);
          hasMore = false; // 停止整个循环，避免“半缺页”继续下去
          break;
        }
        await sleep(backoff);
      }
    }

    if (!resp) break;

    const pageData = resp?.data?.data ?? resp?.data?.rows ?? [];
    const total    = resp?.data?.total ?? resp?.data?.count;
    const lastPageFlag = resp?.data?.isLastPage ?? resp?.data?.lastPage;

    if (!Array.isArray(pageData) || pageData.length === 0) {
      // 空页 => 明确结束
      hasMore = false;
      break;
    }

    if (effectiveFirstPageLen == null) {
      effectiveFirstPageLen = pageData.length; // 记录真实每页大小（后端忽略 pageSize 的情况下）
    }

    allData.push(...pageData);

    // 尾页判断：优先服务端显式标记 / total；没有就依赖“下一页空”策略
    if (lastPageFlag === true) {
      hasMore = false;
    } else if (typeof total === 'number' && allData.length >= total) {
      hasMore = false;
    } else {
      page++;
    }
  }

  return allData;
}

////////////////////////////////设备信息//////////////////////////////////////////////////////////////////
// ========= 设备索引 =========
// 已有：const devicesByKey = new Map()    // key = `${community}__${buildingNo}` -> rows[]

function simplifyDeviceRow(it) {
  // 把后端字段统一一下，表格/调试都好用
  const typeText = it?.type_id1 || it?.type_id?.name || ''
  return {
    device_id:        it?.device_id || '',
    device_name:      it?.device_name || '',
    device_status:    it?.device_status || '',
    type:             typeText,
    ip:               it?.IP || it?.ip || '',
    channel:          it?.Channel_Number || '',
    account:          it?.Account || '',
    port:             it?.Port_Number || '',
    install_location: it?.install_location || '',
    install_time:     it?.install_time || '',
    last_online_time: it?.last_online_time || '',
    Community_Map_Name: it?.Community_Map_Name || it?.community_map_name || '',
    _raw: it
  }
}

// ========= 设备索引（按中文楼名，严格一一对应）=========
const devicesByCnLabel = new Map(); // key = normalizeLabelStrict('和园8#') → rows[]

function buildDeviceCnIndex() {
  devicesByCnLabel.clear();
  const list = Array.isArray(devicePage2.value) ? devicePage2.value : [];
  let withLabel = 0, withoutLabel = 0;

  for (const raw of list) {
    // 统一字段（你已有的简化函数保留，这里只取到 Community_Map_Name）
    const cn = normalizeLabelStrict(
      raw?.Community_Map_Name || raw?.community_map_name || raw?.CommunityMapName || ''
    );

    if (!cn) { withoutLabel++; continue; }

    // 简化后的行（复用你已有的列命名，便于表格展示）
    const dev = simplifyDeviceRow(raw);

    if (!devicesByCnLabel.has(cn)) devicesByCnLabel.set(cn, []);
    devicesByCnLabel.get(cn).push(dev);
    withLabel++;
  }

  // 按类型/名称排一下序，显示更整齐（可选）
  for (const [k, arr] of devicesByCnLabel.entries()) {
    arr.sort((a, b) => {
      const t = (a.type||'').localeCompare(b.type||'', 'zh');
      if (t !== 0) return t;
      return (a.device_name||'').localeCompare(b.device_name||'', 'zh');
    });
  }

  console.groupCollapsed('%c[设备索引·按中文楼名] 构建完成', 'color:#58a6ff');
  console.log('总设备数=', list.length, '有中文楼名=', withLabel, '缺中文楼名=', withoutLabel);
  console.groupEnd();
}




/////////关于设备暴露给主页面////////////////////////////////////////////
// 判定是否“裸设备”（没有中文楼牌名，或有但楼索引里不存在）
function _isLooseDevice(raw) {
  const cn = normalizeLabelStrict(
    raw?.Community_Map_Name || raw?.community_map_name || raw?.CommunityMapName || ''
  );
  if (!cn) return true;
  return !buildingByCnName.has(cn);
}

// 宽松猜测设备所属小区（安装位置 / 设备名 / 中文楼牌前缀）
function _guessCommunity(raw) {
  const pick = s => String(s || '').trim();
  const loc  = pick(raw?.install_location);
  const name = pick(raw?.device_name || raw?.name);
  const lbl  = normalizeLabelStrict(raw?.Community_Map_Name || raw?.community_map_name || '');
  const m = lbl.match(/^(.+?)(\d+)\s*#?$/);
  const prefix = m ? m[1] : '';
  return (prefix || loc || name);
}

// 列出所有“裸设备”（做过 simplifyDeviceRow 的那种）
function listLooseDevices() {
  const list = Array.isArray(devicePage2.value) ? devicePage2.value : [];
  return list.filter(_isLooseDevice).map(simplifyDeviceRow);
}

// 按小区名（宽松包含）拿该小区的裸设备
function listLooseDevicesByCommunity(comm) {
  const want = normalizeCN(comm);
  return listLooseDevices().filter(d => {
    const text = normalizeCN(
      _guessCommunity(d._raw) || d.install_location || d.device_name || ''
    );
    return text.includes(want) || want.includes(text);
  });
}


/** 新增：返回“所有裸设备”（未挂到任何楼） */
async function listLooseDevicesAll() {
  const out = []
  const list = Array.isArray(devicePage2.value) ? devicePage2.value : []

  for (const raw of list) {
    const cn = normalizeLabelStrict(
      raw?.Community_Map_Name || raw?.community_map_name || raw?.CommunityMapName || ''
    )
    // 有中文楼牌且能命中楼索引 → 认为“已挂楼”
    const attached = !!(cn && buildingByCnName.has(cn))
    if (!attached) out.push(simplifyDeviceRow(raw))
  }

  // 排序：类型→设备名
  out.sort((a, b) => {
    const t = (a.type||'').localeCompare(b.type||'', 'zh')
    if (t !== 0) return t
    return (a.device_name||'').localeCompare(b.device_name||'', 'zh')
  })
  return out
}
/////////////////////////////设备暴露完毕//////////////////////



// 对外：根据“中文楼名”直接打开设备弹窗（目录树点击可调用）
async function openDevicesModalForLabel(cnLabel) {
  const key = normalizeLabelStrict(cnLabel || '');
  openModal(`${key} 设备信息`, `<div class="modal-empty">正在加载设备数据...</div>`);
  try {
    const rows = await listDevicesByCnLabel(key);
    modalBox.querySelector('.modal-body').innerHTML = renderDevicesTable(rows);
  } catch (err) {
    modalBox.querySelector('.modal-body').innerHTML =
      `<div class="modal-empty">设备数据加载失败：${err?.message || err}</div>`;
  }
}


/**
 * 把 devicePage2.value（数组）按 “小区+楼号” 落在 devicesByKey
 * 匹配策略：
 *  A) 设备.Community_Map_Name 直接去对楼栋索引 buildingByCnName
 *  B) A 失效 → 解析出中文前缀+楼号，再到 buildingList.value 里找“楼号相等 + 前缀包含”的一条
 *  C) 再不行就丢进一个“裸设备”桶，方便后面人工处理
 */


///////////////////////////////////设备信息结束/////////////////////////////////////////////////////////////////////

async function fetchCommunityData() {
  // 并发获取所有分页数据
  const tasks = [
    // 楼栋信息
    fetchAllPages('/building/list', {
      query_code: 'rkxx_xqld',
      schema_code: 'rkxx_xqld',
      area: AREA,
    }, 
    { pageSize: 50, pageSizeParam: 'pageSize' }),

    // 设备信息
    fetchAllPages('/device/info/list', {
      query_code: 'wlgl_sbxxb',
      schema_code: 'wlgl_sbxxb',
      area: AREA,
      communityName: '',
      buildingNumber: '',
      unitNumber: '',
      houseNumber: '',
      houseType: '',
      houseOwner: '',
      personName: '',
    }, { pageSize: 50, pageSizeParam: 'pageSize' }),
  ]

  const [buildingData, deviceData] = await Promise.allSettled(tasks)

  if (buildingData.status === 'fulfilled') {

    buildingList.value = buildingData.value
    // === 新增：用 Community_Map_Name 建索引（中文名 -> 楼栋记录）
    buildingByCnName.clear();
    for (const it of buildingList.value) {
      const cn = normalizeLabelStrict(it?.Community_Map_Name || '');
      if (cn) buildingByCnName.set(cn, it);
    }

    console.log('✅ 楼栋中文名索引：', buildingByCnName.size, '条');

    rebuildApiIndex()   // —— 新增：建立 Community_Map_Name -> 楼信息 的索引

    buildingsApiByKey.clear()
    buildingsApiByMapName.clear()
    for (const it of buildingList.value) {
      const comm    = normalizeCN(it.Affiliated_Residential_Quart || it.residential_quart || it.communityName || '')
      const no   = normalizeNo(it.Building_Number || it.buildingNumber || '');
      const mapName = normalizeLabel(it.Community_Map_Name || it.communityMapName || '')
      if (comm && no) buildingsApiByKey.set(`${comm}__${no}`, it)
      if (mapName)   buildingsApiByMapName.set(mapName, it)
    }
    console.log('✅ 楼栋索引 OK：', buildingsApiByKey.size, buildingsApiByMapName.size)
    // 绑定接口数据到楼栋节点
    bindApiToBuildings && bindApiToBuildings()


  } else {
    console.warn('楼栋接口失败：', buildingData.reason)
    buildingList.value = []
  }



  if (deviceData.status === 'fulfilled') {
    console.log('DD-小区设备信息返回数量：', deviceData.value.length, '条')
    console.log('DD-小区设备信息返回：', deviceData.value)
    devicePage2.value = deviceData.value

    buildDeviceCnIndex()     // ✅ 新：严格“中文楼名 → 设备列表”索引
  } else {
    console.warn('设备接口失败：', deviceData.reason)
    devicePage2.value = []
  }


}


//弹窗工具（打开 / 关闭 / 渲染居民信息表格） —— 新增：组件挂载时调用
function ensureModal() {
  if (modalOverlay) return
  modalOverlay = document.createElement('div')
  modalOverlay.className = 'modal-overlay'
  modalOverlay.style.display = 'none'

  modalBox = document.createElement('div')
  modalBox.className = 'modal-box'
  modalBox.innerHTML = `
    <div class="modal-header">
      <div class="modal-title"></div>
      <button class="modal-close" aria-label="关闭">✕</button>
    </div>
    <div class="modal-body"></div>
  `
  modalOverlay.appendChild(modalBox)
  document.body.appendChild(modalOverlay)

  // 关闭事件
  modalOverlay.addEventListener('click', (e) => {
    if (e.target === modalOverlay) closeModal()
  })
  modalBox.querySelector('.modal-close').addEventListener('click', closeModal)
}

function openModal(title, htmlContent) {
  ensureModal()
  modalBox.querySelector('.modal-title').textContent = title || ''
  modalBox.querySelector('.modal-body').innerHTML = htmlContent || ''
  modalOverlay.style.display = 'flex'
}

function closeModal() {
  if (modalOverlay) modalOverlay.style.display = 'none'
}


// —— 新增：弹出“裸设备”表（样式与楼里设备表完全一致）
async function openLooseDevicesModal() {
  ensureModal()
  openModal('裸设备 设备信息', `<div class="modal-empty">正在加载设备数据...</div>`)

  try {
    const rows = await listLooseDevicesAll()   // 你已实现并在 defineExpose 暴露
    const html = renderDevicesTable(rows)      // 复用同一张设备表
    modalBox.querySelector('.modal-body').innerHTML = html
  } catch (err) {
    modalBox.querySelector('.modal-body').innerHTML =
      `<div class="modal-empty">设备数据加载失败：${err?.message || err}</div>`
  }
}

//设备表格渲染函数
function renderDevicesTable(rows) {
  if (!rows || rows.length === 0) {
    return `<div class="modal-empty">暂无设备数据</div>`
  }

  // 可见列（按需增删）
  const COLS = [
    { key: 'type',             label: '类型' },
    { key: 'device_name',      label: '设备名称' },
    { key: 'device_status',    label: '状态' },
    { key: 'install_location', label: '安装位置' },
    { key: 'last_online_time', label: '最近在线' },
    { key: 'ip',               label: 'IP' },
    { key: 'channel',          label: '通道' },
    { key: 'account',          label: '账号' },
    { key: 'port',             label: '端口' },
  ]

  // 简单排序：类型 → 名称
  const list = [...rows].sort((a, b) => {
    const t = (a.type||'').localeCompare(b.type||'', 'zh')
    if (t !== 0) return t
    return (a.device_name||'').localeCompare(b.device_name||'', 'zh')
  })

  const thead = `<tr>${COLS.map(c=>`<th>${c.label}</th>`).join('')}</tr>`
  const tbody = list.map(r => `
    <tr>
      ${COLS.map(c=>`<td>${(r[c.key] ?? '-') || '-'}</td>`).join('')}
    </tr>
  `).join('')

  return `
    <div class="table-meta">共 ${list.length} 台设备</div>
    <table class="modal-table">
      <thead>${thead}</thead>
      <tbody>${tbody}</tbody>
    </table>
  `
}



//居民表格渲染函数
function normalizeSexValue(v) {
  const s = String(v ?? '').trim()
  if (!s) return ''
  if (s === '1') return '男'
  if (s === '2') return '女'
  return s
}

function normalizeSearchText(v) {
  return String(v ?? '').trim().replace(/\s+/g, '').toLowerCase()
}

function maskMiddle(str, leftKeep, rightKeep) {
  const s = String(str ?? '').trim()
  if (!s || s === '-') return ''
  const left = Math.max(0, Math.min(leftKeep, s.length))
  const right = Math.max(0, Math.min(rightKeep, s.length - left))
  if (s.length <= left + right) return s
  return `${s.slice(0, left)}${'*'.repeat(s.length - left - right)}${s.slice(s.length - right)}`
}

function maskPhone(phone) {
  const s = String(phone ?? '').trim()
  if (!s || s === '-') return ''
  if (s.length >= 11) return maskMiddle(s, 3, 4)
  if (s.length >= 7) return maskMiddle(s, 2, 2)
  return maskMiddle(s, 1, 1)
}

function maskIdCard(idCard) {
  const s = String(idCard ?? '').trim()
  if (!s || s === '-') return ''
  if (s.length >= 15) return maskMiddle(s, 6, 4)
  if (s.length >= 8) return maskMiddle(s, 2, 2)
  return maskMiddle(s, 1, 1)
}

function createResidentsView(rows) {
  const list = rows.map((r, i) => ({
    unit:            r.unitNumber ?? '',
    house:           r.houseNumber ?? '',
    houseOwner:      r.houseOwner ?? '',
    personName:      r.personName ?? '',
    sex:             normalizeSexValue(r.sex),
    phone:           r.phone ?? '',
    idcard:          r.idcard ?? '',
    houseType:       r.houseType ?? '',
    emptyOrNot:      r.emptyOrNot ?? '',
    nation:          r.nation ?? '',
    education:       r.education ?? '',
    domicilePlace:   r.domicilePlace ?? '',
    workunit:        r.workunit ?? '',
    politicsStatus:  r.politicsStatus ?? '',
    maritalStatus:   r.maritalStatus ?? '',
    deptName:        r.deptName ?? '',
    remark:          r.remark ?? '',
    _raw:            r._raw,
    _idx:            i
  }))

  const toNum = (s) => {
    const m = String(s ?? '').match(/\d+/)
    return m ? parseInt(m[0], 10) : Number.MAX_SAFE_INTEGER
  }

  list.sort((a, b) => {
    const u = toNum(a.unit) - toNum(b.unit)
    if (u !== 0) return u
    const h = toNum(a.house) - toNum(b.house)
    if (h !== 0) return h
    return (a.personName || '').localeCompare(b.personName || '', 'zh')
  })

  const makeKey = (r) => {
    const u = String(r.unit ?? '').trim()
    const h = String(r.house ?? '').trim()
    if (u || h) return `${u}__${h}`
    return `__${r.idcard || r.phone || r.personName || r._idx}`
  }

  const countsAll = new Map()
  const orderKeys = []
  for (const r of list) {
    const key = makeKey(r)
    if (!countsAll.has(key)) { countsAll.set(key, 0); orderKeys.push(key) }
    countsAll.set(key, countsAll.get(key) + 1)
    r._key = key
  }

  const colorClassByKey = new Map()
  orderKeys.forEach((k, i) => colorClassByKey.set(k, i % 2 === 0 ? 'g0' : 'g1'))

  const ALL_COLS = [
    { key: 'unit',           label: '单元' },
    { key: 'house',          label: '门牌' },
    { key: 'houseOwner',     label: '住户' },
    { key: 'personName',     label: '姓名' },
    { key: 'sex',            label: '性别' },
    { key: 'phone',          label: '电话' },
    { key: 'idcard',         label: '证件号' },
    { key: 'houseType',      label: '房屋性质' },
    { key: 'emptyOrNot',     label: '是否空置' },
    { key: 'nation',         label: '民族' },
    { key: 'education',      label: '文化程度' },
    { key: 'domicilePlace',  label: '户籍地' },
    { key: 'workunit',       label: '工作单位' },
    { key: 'politicsStatus', label: '政治面貌' },
    { key: 'maritalStatus',  label: '婚姻状况' },
    { key: 'deptName',       label: '所属部门' },
    { key: 'remark',         label: '备注' },
  ]

  return {
    list,
    countsAll,
    colorClassByKey,
    allCols: ALL_COLS
  }
}

function renderResidentsModalLayout() {
  return `
    <div class="resident-filters">
      <div class="resident-filter-item">
        <span class="resident-filter-label">单元</span>
        <input class="resident-filter-input" data-filter="unit" placeholder="如：1" />
      </div>
      <div class="resident-filter-item">
        <span class="resident-filter-label">门牌</span>
        <input class="resident-filter-input" data-filter="house" placeholder="如：301" />
      </div>
      <div class="resident-filter-item">
        <span class="resident-filter-label">住户</span>
        <input class="resident-filter-input" data-filter="houseOwner" placeholder="如：户主" />
      </div>
      <div class="resident-filter-item">
        <span class="resident-filter-label">姓名</span>
        <input class="resident-filter-input" data-filter="personName" placeholder="如：张" />
      </div>
      <div class="resident-filter-item">
        <span class="resident-filter-label">性别</span>
        <select class="resident-filter-select" data-filter="sex">
          <option value="">全部</option>
          <option value="男">男</option>
          <option value="女">女</option>
        </select>
      </div>
      <div class="resident-filter-item">
        <span class="resident-filter-label">电话</span>
        <input class="resident-filter-input" data-filter="phone" placeholder="如：176" />
      </div>
      <div class="resident-filter-actions">
        <button class="resident-filter-btn" data-action="clear">清空筛选</button>
      </div>
    </div>
    <div class="table-meta resident-meta"></div>
    <div class="resident-table"></div>
    <div class="resident-pagination"></div>
  `
}

function mountResidentsModal(rows) {
  if (!modalBox) return
  if (!rows || rows.length === 0) {
    modalBox.querySelector('.modal-body').innerHTML = `<div class="modal-empty">暂无居民数据</div>`
    return
  }

  const view = createResidentsView(rows)
  const state = {
    view,
    filters: { unit: '', house: '', houseOwner: '', personName: '', sex: '', phone: '' },
    pageSize: 20,
    page: 1
  }

  const body = modalBox.querySelector('.modal-body')
  body.innerHTML = renderResidentsModalLayout()

  const metaEl = body.querySelector('.resident-meta')
  const tableEl = body.querySelector('.resident-table')
  const pagerEl = body.querySelector('.resident-pagination')
  const filtersEl = body.querySelector('.resident-filters')

  const setInputsFromState = () => {
    for (const [k, v] of Object.entries(state.filters)) {
      const el = body.querySelector(`[data-filter="${k}"]`)
      if (!el) continue
      el.value = v
    }
  }

  const applyFilters = (list) => {
    const f = state.filters
    const qUnit = normalizeSearchText(f.unit)
    const qHouse = normalizeSearchText(f.house)
    const qOwner = normalizeSearchText(f.houseOwner)
    const qName = normalizeSearchText(f.personName)
    const qSex = normalizeSearchText(f.sex)
    const qPhone = normalizeSearchText(f.phone)

    return list.filter(r => {
      if (qUnit && !normalizeSearchText(r.unit).includes(qUnit)) return false
      if (qHouse && !normalizeSearchText(r.house).includes(qHouse)) return false
      if (qOwner && !normalizeSearchText(r.houseOwner).includes(qOwner)) return false
      if (qName && !normalizeSearchText(r.personName).includes(qName)) return false
      if (qSex && normalizeSearchText(r.sex) !== qSex) return false
      if (qPhone && !normalizeSearchText(r.phone).includes(qPhone)) return false
      return true
    })
  }

  const render = () => {
    const fullList = state.view.list
    const filteredList = applyFilters(fullList)
    const personsTotal = fullList.length
    const householdsTotal = state.view.countsAll.size

    const filteredHouseholds = new Set(filteredList.map(r => r._key)).size

    const totalPages = Math.max(1, Math.ceil(filteredList.length / state.pageSize))
    if (state.page > totalPages) state.page = totalPages
    if (state.page < 1) state.page = 1

    const start = (state.page - 1) * state.pageSize
    const pageRows = filteredList.slice(start, start + state.pageSize)

    metaEl.textContent = `共 ${personsTotal} 人，户数 ${householdsTotal}；当前筛选 ${filteredList.length} 人，${filteredHouseholds} 户`

    const cols = state.view.allCols.filter(c => {
      if (['unit', 'house', 'personName'].includes(c.key)) return true
      return filteredList.some(r => String(r[c.key] ?? '').trim() !== '')
    })
    const heads = ['序号', '同门人数', ...cols.map(c => c.label)]

    let idx = start
    const trs = pageRows.map(r => {
      idx++
      const gCount = state.view.countsAll.get(r._key) ?? 1
      const cls = state.view.colorClassByKey.get(r._key) || ''
      const tds = cols.map(c => {
        const v = r[c.key]
        if (c.key === 'phone') return `<td>${maskPhone(v) || '-'}</td>`
        if (c.key === 'idcard') return `<td>${maskIdCard(v) || '-'}</td>`
        return `<td>${(v ?? '') || '-'}</td>`
      }).join('')
      return `
        <tr class="${cls}">
          <td class="num">${idx}</td>
          <td class="gcount">${gCount}</td>
          ${tds}
        </tr>`
    }).join('')

    tableEl.innerHTML = `
      <table class="modal-table">
        <thead><tr>${heads.map(h=>`<th>${h}</th>`).join('')}</tr></thead>
        <tbody>${trs || `<tr><td colspan="${heads.length}" class="modal-empty">暂无数据</td></tr>`}</tbody>
      </table>
    `

    const makePageBtn = (p) => {
      const active = p === state.page ? ' is-active' : ''
      return `<button class="resident-page-btn${active}" data-action="page" data-page="${p}">${p}</button>`
    }

    const pages = []
    const windowSize = 7
    const half = Math.floor(windowSize / 2)
    let left = Math.max(1, state.page - half)
    let right = Math.min(totalPages, left + windowSize - 1)
    left = Math.max(1, right - windowSize + 1)
    for (let p = left; p <= right; p++) pages.push(p)

    pagerEl.innerHTML = `
      <div class="resident-pager">
        <button class="resident-page-nav" data-action="prev" ${state.page <= 1 ? 'disabled' : ''}>上一页</button>
        <div class="resident-page-list">${pages.map(makePageBtn).join('')}</div>
        <button class="resident-page-nav" data-action="next" ${state.page >= totalPages ? 'disabled' : ''}>下一页</button>
        <div class="resident-page-info">第 ${state.page} / ${totalPages} 页（每页 ${state.pageSize} 条）</div>
      </div>
    `
  }

  filtersEl.addEventListener('input', (e) => {
    const target = e.target
    if (!target) return
    const key = target.getAttribute('data-filter')
    if (!key) return
    state.filters[key] = target.value
    state.page = 1
    render()
  })

  filtersEl.addEventListener('change', (e) => {
    const target = e.target
    if (!target) return
    const key = target.getAttribute('data-filter')
    if (!key) return
    state.filters[key] = target.value
    state.page = 1
    render()
  })

  filtersEl.addEventListener('click', (e) => {
    const target = e.target
    if (!target) return
    const action = target.getAttribute('data-action')
    if (action !== 'clear') return
    state.filters = { unit: '', house: '', houseOwner: '', personName: '', sex: '', phone: '' }
    state.page = 1
    setInputsFromState()
    render()
  })

  pagerEl.addEventListener('click', (e) => {
    const target = e.target
    if (!target) return
    const action = target.getAttribute('data-action')
    if (action === 'prev') {
      state.page = Math.max(1, state.page - 1)
      render()
      return
    }
    if (action === 'next') {
      state.page = state.page + 1
      render()
      return
    }
    if (action === 'page') {
      const p = parseInt(target.getAttribute('data-page') || '', 10)
      if (!Number.isFinite(p)) return
      state.page = p
      render()
    }
  })

  setInputsFromState()
  render()
}


//////////////////////关于接口的数据的对接结束///////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////

// 导入localforage用于模型缓存
import localforage from 'localforage'

const container = ref(null)
const isLoading = ref(true)
const isRotating = ref(false)
const selectedObjectName = ref('')
const loadingProgress = ref(0)
const currentLoadingPart = ref('')
// const loadedParts = ref([])

// —— 居民信息：索引表（key = `${communityName}__${buildingNumber}`）
// === 新增：规范化 & 索引 ===
const buildingByCnName = new Map();   // '佳园7#' -> 楼栋记录（来自楼栋列表接口）
const residentsByKey = new Map()
// // key = `${communityName}__${buildingNumber}` -> [居民记录数组]
// function normalizeCN(s = '') {// 去除空格，统一＃为#
//   return String(s)
//     .trim()
//     .replace(/\s+/g, '')
//     .replace(/＃/g, '#')
//     .replace(/号楼$/,'#') // '7号楼' -> '7#'
// }

// function normalizeNo(n) {
//   return String(n ?? '').trim().replace(/^0+/, '');
// // }
// function makeKey(communityName, buildingNumber) {
//   return `${normalizeCN(communityName)}__${normalizeNo(buildingNumber)}`;
// }


// // ==== 临时演示配置（先用固定楼：保利佳园A区 / 1号楼）====
// const DEMO_RESIDENT_KEY = { communityName: '保利佳园A区', buildingNumber: '1' }
// // 设为 true 表示强制用上面的固定楼；设为 false 表示优先用当前楼名匹配，匹配不到再用固定楼兜底
// const FORCE_USE_DEMO = false


// —— （可选）设备信息：如果后面要做第二个按钮，也用这个 Map
const devicesByKey = new Map()

// 排除草/树/路等大类的前缀（小写）
const buildingsByName = new Map()  // 名称 -> 楼节点


let scene, camera, controls, raycaster, coverGroup
let renderer = null
let ddModel = null
let rotationSpeed = 0.005
let animationId = null
let mouse = new THREE.Vector2()
let modelParts = {} // 存储所有模型部分
let visibleParts = new Set() // 当前可见的模型部分
let dirty = true                               // ← 按需渲染标记
const BASE_PR = Math.min(window.devicePixelRatio, 1.0) // ← 基础像素比（别太高）
const INITIAL_CAMERA_POS = new THREE.Vector3(20, 20, 25)
const INITIAL_CAMERA_TARGET = new THREE.Vector3(-10, 10, -10)


// —— 弹窗 DOM 引用
let modalOverlay = null
let modalBox = null

let labelRenderer = null                 // ← 用于气泡的 2D 渲染器
let clickableBuildings = []              // ← 可点击“楼”的根节点集合
let currentHelper = null                 // ← 当前高亮包围框
let currentLabel = null                  // ← 当前气泡
const buildingMeta = new Map()           // ← 楼的元数据（可对接你的接口
const apiByKey     = new Map()           // 新增：接口数据的索引表


let gltfLoader = new GLTFLoader()
let ktx2 = new KTX2Loader().setTranscoderPath('/basis/')
gltfLoader.setMeshoptDecoder(MeshoptDecoder)

// 配置localforage
localforage.config({
  name: 'DDModelCache',
  storeName: 'modelParts',
  description: '缓存DD模型的各个部分以提高加载速度'
})

// 检查浏览器内存限制
function checkMemoryLimit() {
  try {
    // 尝试分配一定大小的ArrayBuffer来测试内存可用性
    // eslint-disable-next-line no-unused-vars
    const testBuffer = new ArrayBuffer(1024 * 1024 * 200); // 200MB测试
    return true;
  } catch (e) {
    console.warn('浏览器内存可能不足，大型模型加载可能失败')
    return false;
  }
}


// 初始化场景
function initScene() {
  // 1) 场景
  scene = new THREE.Scene()

  // === 覆盖物容器（白膜会统一加到这里） ===
  coverGroup = new THREE.Group()
  coverGroup.name = 'CoverGroup'
  scene.add(coverGroup)


  // 可用内存简单自检（保留）
  checkMemoryLimit()

  // 2) 全景背景
  const styleElement = document.createElement('style')
  styleElement.textContent = `
    .panorama-container {
      width: 100%;
      height: 100%;
      position: relative;
      overflow: hidden;
    }
  `
  document.head.appendChild(styleElement)

  const panoramaGeometry = new THREE.SphereGeometry(500, 60, 40)
  const panoramaTexture = new THREE.TextureLoader().load('/models/5.jpg')
  panoramaTexture.wrapS = THREE.RepeatWrapping
  panoramaTexture.wrapT = THREE.RepeatWrapping
  panoramaTexture.repeat.set(1, 1)
  const panoramaMaterial = new THREE.MeshBasicMaterial({
    map: panoramaTexture,
    side: THREE.BackSide
  })
  const panorama = new THREE.Mesh(panoramaGeometry, panoramaMaterial)
  scene.add(panorama)
  scene.userData.panorama = panorama

  // 3) 相机（用容器尺寸而不是 window，避免初始拉伸）
  const w = container.value.clientWidth
  const h = container.value.clientHeight
  camera = new THREE.PerspectiveCamera(60, w / h, 0.1, 2000)
  camera.position.copy(INITIAL_CAMERA_POS)
  camera.lookAt(INITIAL_CAMERA_TARGET)

  // 4) 渲染器（只创建一次，不要重复 append）
  try {
    renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
      failIfMajorPerformanceCaveat: false // 允许在性能不佳的情况下运行
    })
  } catch (e) {
    console.error('WebGL 初始化失败，尝试基础模式:', e)
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: false,
        alpha: true
      })
    } catch (e2) {
      console.error('WebGL 完全不可用:', e2)
      isLoading.value = false
      currentLoadingPart.value = '浏览器不支持 WebGL'
      return
    }
  }

  if (!renderer) return

  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.2))  // 降分辨率防爆显存
  renderer.setSize(w, h)
  renderer.shadowMap.enabled = false
  container.value.appendChild(renderer.domElement)


  // === 2D 标签渲染器（气泡用） ===
  labelRenderer = new CSS2DRenderer()
  labelRenderer.setSize(container.value.clientWidth, container.value.clientHeight)
  labelRenderer.domElement.style.position = 'absolute'
  labelRenderer.domElement.style.top = '0'
  labelRenderer.domElement.style.pointerEvents = 'none' // 让鼠标事件继续传给 3D
  container.value.appendChild(labelRenderer.domElement)
  injectBubbleStyle() // 注入气泡样式


  // 5) 轨道控制器
  controls = new OrbitControls(camera, renderer.domElement)
  controls.enableDamping = true
  controls.dampingFactor = 0.05
  controls.maxPolarAngle = Math.PI * 0.48
  controls.minDistance = 5
  controls.maxDistance = 400
  controls.enableZoom = true
  controls.autoRotate = false
  controls.target.copy(INITIAL_CAMERA_TARGET)
  controls.update()
  controls.addEventListener('start', () => setPR(0.75)) // 开始交互降像素比
  controls.addEventListener('end',   () => setPR(BASE_PR)) // 结束交互恢复
  controls.addEventListener('change', markDirty) // 变化时标记需要重绘


  // 6) 纹理/网格解码器
  if (!ktx2) ktx2 = new KTX2Loader().setTranscoderPath('/basis/')
  if (!gltfLoader) {
    gltfLoader = new GLTFLoader()
    gltfLoader.setMeshoptDecoder(MeshoptDecoder)
  }
  ktx2.detectSupport(renderer)
  gltfLoader.setKTX2Loader(ktx2)

  // 7) 加载模型
  isLoading.value = true
  loadingProgress.value = 0
  currentLoadingPart.value = '构建3D社区实景'
  gltfLoader.load(
    '/models/Astral3DScene_vjing_pack.glb',
   async (gltf) => {
      if (isDestroyed || !scene) return;
      ddModel = gltf.scene
      scene.add(ddModel)
      
      // 环绕一圈摆放白膜

      // 放在：scene.add(ddModel) 之后、ddModel 已经 add 到场景的同一位置
      placeBjRingSimple(ddModel, {
        count: 20,                 // 总数
        scale: 0.15,
        yRatio: 0.65,
        gap: 30,                    // 同边白膜之间稍微拉开一点
        offset: { left: 35, right: -40, front: 20, back: -80 },

        // ★ 侧边权重：前/后比左/右多
        sideWeights: { front: 15, back: 10, left: 1, right: 1 },

        // ★ 角落占 25% 的量，角落内部：东北/西北更大
        cornerPortion: 0.25,
        cornerWeights: { ne: 2, nw: 2, se: 1, sw: 1 },

        // 角落沿对角线向外排的起点/步距（按需可略调）
        cornerOffset: 0,
        cornerSpan: 0,             // 0 = 用默认的 (W + gap)

        rotateY90: true,
        clearBefore: true,
      })


      // ✅ 先收集“楼”节点（命名示例：L1-3、L2-12…）
      collectBuildings(ddModel)
      console.log('可点击楼数量:', clickableBuildings.length, clickableBuildings.map(n => n.name))
      bindApiToBuildings && bindApiToBuildings()

      // 触发楼栋更新事件
      emit('buildings-update', clickableBuildings.map(b => b.name))



      freezeStatic(ddModel)
      // ❗重要：不要把“楼”合批，否则无法逐栋点击。给 instanceDuplicates 传“排除函数”
      // instanceDuplicates(ddModel, (o) => isBuildingNode(o))

      //计算一个围绕 ddModel 的矩形做为人/车活动范围
      function rectAround(object3D, pad = 8) {
        const box = new THREE.Box3().setFromObject(object3D)
        return {
          x: [box.min.x - pad, box.max.x + pad],
          z: [box.min.z - pad, box.max.z + pad],
          y: box.min.y
        }
      }


     // —— 新增：创建云/人/车（都基于 ddModel 包围）
    cloudsCtl  = createClouds(scene, { around: ddModel, count: 14, height: 80, spread: 320 })
    
    // 这里改为 await，确保 crowdCtl 不是 Promise
    try {
      crowdCtl = await createCrowd(scene, ddModel, { glbUrl: '/models/man.glb', count: 60 })
    } catch (e) {
      console.warn('行人加载失败:', e)
    }
    
    trafficCtl = createTraffic(scene, ddModel, { cars: 8, pad: 8 })




      markDirty()

      isLoading.value = false
      currentLoadingPart.value = ''
    },
    (xhr) => {
      if (xhr.lengthComputable && xhr.total > 0) {
        loadingProgress.value = Math.min(100, Math.round(xhr.loaded / xhr.total * 100))
      } else {
        // 服务器没返回 Content-Length 就做“平滑假进度”，避免 0% 卡住的错觉
        loadingProgress.value = Math.min(95, (loadingProgress.value || 0) + 0.5)
      }
    },
    (err) => {
      console.error('GLB 加载失败：', err)
      isLoading.value = false
      currentLoadingPart.value = '加载失败'
    }
  )

  // 8) 交互/光照/地面（保留你原来的调用）
  raycaster = new THREE.Raycaster()
  addLights && addLights()
  addGround && addGround()
  // addGridHelper && addGridHelper()

  // 9) 事件
  window.addEventListener('resize', onWindowResize)
  container.value.addEventListener('click', onMouseClick)

  // 初次渲染一帧
  renderOnce()
  // ← 新增：启动轻量循环（带阻尼 update + 按需渲染）
  animate()
}


////////////////////////白膜的导入用的功能///////////////////////////////////////////////////////////
// 计算任意模型的包围盒尺寸
function getSize3(obj) {
  const box = new THREE.Box3().setFromObject(obj)
  const size = new THREE.Vector3()
  box.getSize(size)
  return { box, size }
}

// 把模型材质替换成“白膜”
function makeWhiteMembrane(obj) {
  obj.traverse((n) => {
    if (n.isMesh) {
      n.material = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.35,
        roughness: 0.9,
        metalness: 0.0,
        depthWrite: false,     // 不写入深度，避免遮挡太狠
        emissive: 0x101010
      })
    }
  })
}

// 额外旋转：Y 轴再转 90°
const ROTATE_Y_90 = true
const EXTRA_ROT_Y = THREE.MathUtils.degToRad(90)

//复制很多份的方法
// 简单版：在 DD 四周均匀摆放白膜（环绕一圈）
// 环绕 DD 摆白膜 + 支持“前后/左右权重 & 四角权重”
function placeBjRingSimple(dd, opt = {}) {
  const {
    count = 10,
    scale = 0.12,
    yRatio = 0.6,
    offset = { left: 0, right: 0, front: 0, back: 0 },
    gap = 0,
    rotateY90 = true,
    clearBefore = true,
    // ★ 新增：分配权重
    sideWeights = { front: 1, back: 1, left: 1, right: 1 }, // 边的密度
    cornerPortion = 0,                                       // 0~1，给四个角占比
    cornerWeights = { ne: 1, nw: 1, se: 1, sw: 1 },          // 四角密度
    cornerOffset = 0,                                        // 从拐角向外退一点
    cornerSpan = 0,                                          // 同一角内相邻的间距（为 0 用步长）
  } = opt

  if (clearBefore) coverGroup?.clear()

  gltfLoader.load('/models/bj.glb', (gltf) => {
    const tpl = gltf.scene
    const { size: ddSize } = getSize3(dd)
    const { size: rawBjSize } = getSize3(tpl)

    let W = rawBjSize.x * scale
    let D = rawBjSize.z * scale
    if (rotateY90) { const t = W; W = D; D = t }

    const halfW = ddSize.x / 2
    const halfD = ddSize.z / 2

    const dist = {
      left:  -(halfW + W / 2 + (offset.left  || 0)),
      right:  (halfW + W / 2 + (offset.right || 0)),
      front: -(halfD + D / 2 + (offset.front || 0)), // -Z
      back:   (halfD + D / 2 + (offset.back  || 0)), // +Z
    }

    // 工具：按权重把 total 分成整数，每份 >= 0，保证总和 == total
    const splitByWeights = (total, weightsObj) => {
      const keys = Object.keys(weightsObj)
      const arr = keys.map(k => ({ k, w: Math.max(0, Number(weightsObj[k]) || 0) }))
      const sum = arr.reduce((s, a) => s + a.w, 0)
      if (sum === 0) return Object.fromEntries(keys.map(k => [k, 0]))
      // 先取地板
      const raw = arr.map(a => ({ k: a.k, v: Math.floor(total * a.w / sum) }))
      let used = raw.reduce((s, a) => s + a.v, 0)
      // 余数按“最大小数部分”分配
      const frac = arr
        .map(a => ({ k: a.k, f: total * a.w / sum - Math.floor(total * a.w / sum) }))
        .sort((a, b) => b.f - a.f)
      let i = 0
      while (used < total && i < frac.length) {
        const key = frac[i].k
        const r = raw.find(x => x.k === key); r.v += 1; used += 1; i += 1
      }
      return Object.fromEntries(raw.map(a => [a.k, a.v]))
    }

    // 先给角落一部分数量
    const cornerTotal = Math.max(0, Math.min(count, Math.round(count * cornerPortion)))
    const sideTotal   = count - cornerTotal

    const sideCount   = splitByWeights(sideTotal, sideWeights) // {front, back, left, right}
    const cornerCount = splitByWeights(cornerTotal, cornerWeights) // {ne,nw,se,sw}

    // 克隆一个白膜并放置（局部 → 世界）
    const makeOne = (xLocal, zLocal) => {
      const m = tpl.clone(true)
      makeWhiteMembrane(m)
      m.scale.set(dd.scale.x * scale, dd.scale.y * scale * yRatio, dd.scale.z * scale)
      m.quaternion.copy(dd.quaternion)
      if (rotateY90) m.rotateY(Math.PI / 2)
      const off = new THREE.Vector3(xLocal, 0, zLocal).applyQuaternion(dd.quaternion)
      m.position.copy(dd.position).add(off)
      coverGroup.add(m)
    }

    // 在某一边均匀摆 k 个，沿边长均分（留两端空隙）
    const distributeAlongEdge = (k, usableLen, fixed, alongIsX) => {
      if (k <= 0) return
      const step = usableLen / (k + 1)
      for (let i = 1; i <= k; i++) {
        const t = -usableLen / 2 + step * i
        if (alongIsX) makeOne(t, fixed)
        else          makeOne(fixed, t)
      }
    }

    // 侧边的可用长度（把 gap 叠加进去）
    const usableZ = ddSize.z + gap * Math.max(0, (sideCount.left + sideCount.right) - 2)
    const usableX = ddSize.x + gap * Math.max(0, (sideCount.front + sideCount.back) - 2)

    // 先摆四边
    distributeAlongEdge(sideCount.left,  usableZ, dist.left,  false) // 沿 Z
    distributeAlongEdge(sideCount.right, usableZ, dist.right, false)
    distributeAlongEdge(sideCount.front, usableX, dist.front, true)  // 沿 X
    distributeAlongEdge(sideCount.back,  usableX, dist.back,  true)

    // 四个角：在 45° 斜线方向“往外排”，数量按 cornerCount
    const diagStepX = (cornerSpan || (W + gap)) * Math.SQRT1_2
    const diagStepZ = (cornerSpan || (D + gap)) * Math.SQRT1_2

    const cornerStart = {
      ne: { x:  halfW + W/2 + (offset.right||0)  + cornerOffset, z: -(halfD + D/2 + (offset.front||0)) - cornerOffset },
      nw: { x: -(halfW + W/2 + (offset.left ||0)) - cornerOffset, z: -(halfD + D/2 + (offset.front||0)) - cornerOffset },
      se: { x:  halfW + W/2 + (offset.right||0)  + cornerOffset, z:  (halfD + D/2 + (offset.back ||0)) + cornerOffset },
      sw: { x: -(halfW + W/2 + (offset.left ||0)) - cornerOffset, z:  (halfD + D/2 + (offset.back ||0)) + cornerOffset },
    }
    const signs = { ne: {sx: +1, sz: -1}, nw: {sx: -1, sz: -1}, se: {sx: +1, sz: +1}, sw: {sx: -1, sz: +1} }

    const placeCorner = (key, k) => {
      if (!k) return
      const base = cornerStart[key]; const s = signs[key]
      for (let i = 0; i < k; i++) {
        const x = base.x + (i + 1) * diagStepX * s.sx
        const z = base.z + (i + 1) * diagStepZ * s.sz
        makeOne(x, z)
      }
    }

    placeCorner('ne', cornerCount.ne)
    placeCorner('nw', cornerCount.nw)
    placeCorner('se', cornerCount.se)
    placeCorner('sw', cornerCount.sw)
  })
}

///////////////白膜导入用的函数/////////////////////////////////////////////////////////////////////////

// 命中网格后向上爬到“登记过的楼”祖先
function climbToRegisteredBuilding(obj) {
  let p = obj
  while (p && p !== ddModel) {
    if (p.userData?.isBuilding) return p
    p = p.parent
  }
  return null
}

//点击导航栏后飞到对应的楼函数
function easeInOutCubic(t){ return t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2,3)/2 }

function flyToObject(object, {
  duration = 1200,          // 飞行时长(ms)
  padding  = 12.0,           // 取景留白
  offset   = new THREE.Vector3(1, 0.6, 1) // 观察方向（可改成你喜欢的角度）
} = {}) {
  const box = new THREE.Box3().setFromObject(object)
  const size = box.getSize(new THREE.Vector3())
  const center = box.getCenter(new THREE.Vector3())

  // 计算相机到对象中心的距离以完整装入画面
  const maxSize = Math.max(size.x, size.y, size.z)
  const fitHeight = maxSize / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5)))
  const fitWidth  = fitHeight / camera.aspect
  const distance  = Math.max(fitHeight, fitWidth) * padding

  const dir = offset.clone().normalize()
  const targetPos = center.clone().add(dir.multiplyScalar(distance))
  const startPos = camera.position.clone()
  const startTarget = controls.target.clone()

  const start = performance.now()
  controls.enabled = false

  ;(function step(){
    const t = Math.min(1, (performance.now() - start) / duration)
    const e = easeInOutCubic(t)

    camera.position.lerpVectors(startPos, targetPos, e)
    controls.target.lerpVectors(startTarget, center, e)
    camera.updateProjectionMatrix()
    markDirty()

    if (t < 1) requestAnimationFrame(step)
    else controls.enabled = true
  })()
}



//对外暴露的点击导航栏飞过去的函数
// 通过 L1-3 / L1_3 这种真实名称聚焦
function focusBuildingByName(name) {
  if (!ddModel) return
  // 兼容 -/_ 与 01 补零
  const candidates = [
    name, name.replace('-', '_'), name.replace('_', '-')
  ]
  let node = null
  for (const n of candidates) {
    node = buildingsByName.get(n) || ddModel.getObjectByName(n)
    if (node) break
  }
  if (!node) return console.warn('楼未找到:', name)



  highlightBuilding(node)
  showBubble(node)
  flyToObject(node, { duration: 1200, padding: 1.8 })
}

// 通过“1号楼/2号楼”这种中文导航名聚焦（你右侧导航）
function focusBuildingByLabel(label) {
  const m = String(label).match(/(\d+)/)
  if (!m) return console.warn('无法解析楼号:', label)
  const i = parseInt(m[1], 10)
  const base = String(i).padStart(2, '0')
  const names = [`L1-${i}`, `L1_${i}`, `L1-${base}`, `L1_${base}`]
  for (const n of names) {
    if (buildingsByName.has(n) || ddModel.getObjectByName(n)) {
      return focusBuildingByName(n)
    }
  }
  console.warn('没有与导航匹配的楼:', label)
}


function listAllBuildings() {
  return clickableBuildings.map(n => n.name)
}

// 列出某个中文楼名对应的设备列表
// === 设备查询：通过中文楼名（如“和园8#”） → 设备数组 ===
async function listDevicesByCnLabel(cnLabel) {
  const key = normalizeLabelStrict(cnLabel || '');

  // ① 主索引：直接按中文楼名取
  const arr = devicesByCnLabel.get(key);
  if (Array.isArray(arr) && arr.length) return arr;

  // ② 兜底：通过楼索引 → 拼出“小区+楼号”key，再从 devicesByKey 取（如果后面你需要）
  const bRec = buildingByCnName.get(key);
  if (bRec) {
    const community = normalizeCN(bRec.Affiliated_Residential_Quart || bRec.communityName || '');
    const bnum      = normalizeNo(bRec.Building_Number || bRec.buildingNumber || '');
    const k2        = makeKey(community, bnum);
    const arr2 = devicesByKey.get(k2);
    if (Array.isArray(arr2) && arr2.length) return arr2;
  }

  // ③ 最后兜底：直接扫接口原数组，防止索引不同步
  const direct = (Array.isArray(devicePage2.value) ? devicePage2.value : [])
    .filter(d => normalizeLabelStrict(d?.Community_Map_Name || d?.community_map_name || '') === key)
    .map(simplifyDeviceRow);

  return direct;
}


// 给父组件/外部调用
defineExpose({ focusBuildingByName, focusBuildingByLabel, listAllBuildings , 
  listDevicesByCnLabel,openDevicesModalForLabel
, // 新增：
  listLooseDevices, listLooseDevicesByCommunity, listLooseDevicesAll, openLooseDevicesModal,   // ← 新增这一行
})// ⬇ 新增暴露：硬件树需要绑定接口数据


// 小工具：按需渲染一帧
function renderOnce() {
  if (renderer && scene && camera) renderer.render(scene, camera)
}

// ========== 轻量渲染控制 ==========
function setPR (v) { if (renderer) { renderer.setPixelRatio(v); markDirty() } }
function markDirty () { dirty = true }
function renderNow () {
  renderer.render(scene, camera)
  if (labelRenderer) labelRenderer.render(scene, camera)
  dirty = false
}


function animate () {
  animationId = requestAnimationFrame(animate)

  if (controls && controls.enableDamping) controls.update()

  if (isRotating.value && controls) dirty = true

   // —— 新增：更新挂件（有位移就标记重绘）人云车
  const delta = clock.getDelta()
  const elapsed = clock.getElapsedTime()
  if (cloudsCtl?.update?.(delta, elapsed)) dirty = true
  if (crowdCtl?.update?.(delta)) dirty = true
  if (trafficCtl?.update?.(delta)) dirty = true


  if (dirty) renderNow()
}



// 鼠标点击事件处理函数
function onMouseClick(event) {
  if (!container.value) return
  const rect = container.value.getBoundingClientRect()
  const nx = ((event.clientX - rect.left) / rect.width) * 2 - 1
  const ny = -((event.clientY - rect.top) / rect.height) * 2 + 1
  mouse.set(nx, ny)

  raycaster.setFromCamera(mouse, camera)

  // 只对“登记的楼”集合做相交；提高命中率和性能
  const hits = clickableBuildings.length
    ? raycaster.intersectObjects(clickableBuildings, true)
    : (ddModel ? raycaster.intersectObject(ddModel, true) : [])

  if (!hits.length) { clearSelection(); return }

  // 命中的可能是子 Mesh，向上爬到楼的根
  const building = climbToRegisteredBuilding(hits[0].object)
  if (!building) { clearSelection(); return }

  highlightBuilding(building)
  showBubble(building)

  selectedObjectName.value = building.name || ''
  emit('object-click', { objectName: selectedObjectName.value, objectType: building.type })
}


// =============== 辅助函数们 =================

// 判断是否“楼”节点（按命名规则：L1-3、L2-12...）
// function isBuildingName(name = '') {
//   const n = String(name).toLowerCase().trim()
//   return /^l\d+-\d+$/i.test(n)  // 你模型里像 L1-3、L1-6 这种
// }
// ——【楼体识别规则】———————————————


// 排除的前缀：草地/树/道路
const EXCLUDE_PREFIXES = ['cao-', 'tree-', 'gl-','tree', 'cao', 'top', 'DM', 'Astral3DScene',
  'material', 'texture_pbr', 'Plane', 'ground', 'leaves']

  // ⬇⬇⬇ 在这里粘贴新增这一行 ⬇⬇⬇
// const BUILDING_CN_RE = /^(善园|佳园|和园|臻园)\d+#$/;
// 中文楼名：① 善园/佳园 + 数字 + #   ② 善园/佳园党群服务中心
const BUILDING_CN_RE = /^(?:(?:善园|佳园|和园|臻园)\d+#|(?:善园|佳园|和园|臻园)党群服务中心|(?:善园|佳园|和园|臻园)幼儿园|(?:善园|佳园|和园|臻园)售楼处)$/;


// 叶子“楼体”命名：L/M/R + 楼号（支持 L6-G、L7-G1、R3-10 这类），以及 DM-* / top-*
// 叶子“楼体”命名：现在以中文楼名为准；兼容保留旧规则
function isLeafBuildingName(name = '') {
  const n = String(name).trim();           // 不转小写，中文不受大小写影响

  // ① 新规则：中文楼名（善园/佳园 + 数字 + #）
  if (BUILDING_CN_RE.test(n)) return true;

  // ② 兼容旧模型（可保留，防止有残留的 L/M/R、DM、top 等仍想点击）
  if (/^[lmr]\d+-[a-z0-9]+$/i.test(n)) return true;  // L2-3 / L6-G / L7-G2 / M1-2 / R3-10...
  if (/^dm-\d+$/i.test(n)) return true;              // DM-1 / DM-2...
  if (/^top-[a-z0-9-_.]+$/i.test(n)) return true;    // top-A / top-B / top-C...

  return false;
}


// 是否有“叶子楼体”后代（不限直系孩子）
function hasLeafDescendant(node) {
  let found = false
  node.traverse((c) => {
    if (c !== node && isLeafBuildingName(c.name)) found = true
  })
  return found
}

// 是否有 Mesh 后代（用来判断“单体楼”：自身不含叶子楼名，但确有几何体）
function hasMeshDescendant(node) {
  let found = false
  node.traverse((c) => {
    if (c.isMesh) found = true
  })
  return found
}

// 是否应把“当前节点”注册为一个楼体
// 规则：
// 1) 只要名字是叶子楼名 => 直接注册（如 L1-1 / DM-1 / top-A）
// 2) 名字以 jianzhu-/DM-/top- 开头，且【没有】叶子后代，但有 Mesh 后代 => 视为“单体楼”，注册它本身（如 jianzhu-L5-1 / jianzhu-R1-3 / jianzhu-L）
function shouldRegisterThisNode(node) {
  const name = (node.name || '').toLowerCase()
  if (!name) return false

  if (isLeafBuildingName(name)) return true

  const isContainerLike = (
    name.startsWith('jianzhu-') ||
    name.startsWith('dm-') ||
    name.startsWith('top-')
  )

  if (isContainerLike) {
    if (!hasLeafDescendant(node) && hasMeshDescendant(node)) {
      return true  // 单体
    }
    return false   // 容器，具体的子楼会在遍历时各自被注册
  }

  return false
}

// 收集所有“楼体”节点（按规则），放到 clickableBuildings 和 buildingsByName
function collectBuildings(root) {
  // 清空旧状态（保持你原来的变量名）
  clickableBuildings.length = 0
  buildingsByName.clear()

  const visited = new Set()

  root.traverse((node) => {
    if (!(node && (node.isObject3D || node.isGroup))) return

    const name = node.name || ''
    const lower = name.toLowerCase()

    // 排除草/树/道路等大类
    if (EXCLUDE_PREFIXES.some((p) => lower.startsWith(p))) return

    if (!shouldRegisterThisNode(node)) return
    if (visited.has(node)) return

    // 标记为“楼体”
    node.userData.isBuilding = true
    clickableBuildings.push(node)
    buildingsByName.set(name, node)

    // 兼容你之前的元数据逻辑（可保留/可删）
    if (!buildingMeta.has(name)) {
      buildingMeta.set(name, { id: name, community: '荆泉北路社区-和善园', buildingType: '居民楼', households: 96, floors: 7, unitsPer: 4, manager: '何洪其', tel: '18606328183', remark: '' })
    }

    visited.add(node)
  })

  console.log('✅ 已注册楼体数量:', clickableBuildings.length,
              '示例:', clickableBuildings.slice(0, 10).map(n => n.name))
}


// 高亮包围框
function highlightBuilding(building) {
  if (currentHelper) {
    scene.remove(currentHelper)
    currentHelper.geometry.dispose()
    currentHelper.material.dispose()
    currentHelper = null
  }
  currentHelper = new THREE.BoxHelper(building, 0xffff00)
  scene.add(currentHelper)
  markDirty()
}

//楼栋信息对接函数
// —— 新增：从模型的子节点里找中文+楼号的叶子名（如“善园47# / 臻1#”）
function getModelCnLabel (node) {
  let r = null
  node.traverse((c) => {
    if (r || c === node) return
    const nm = c.name || ''
    if (/[\u4e00-\u9fa5]/.test(nm) && /[#＃]/.test(nm)) r = nm
    // 逻辑： 当字符串同时满足以下两个条件时，将其赋值给 r：
    // 包含中文字符（/[\u4e00-\u9fa5]/）
    // 包含井号（/[#＃]/）
  })
  return r
}


/////////////////对接居民信息的函数/////////////////////////////////////////////////////////////////////////
// —— 新增：把接口中的“楼栋信息”贴到每个 3D 楼节点的 meta 上，这个就是将接口的楼栋信息绑定到3D模型的楼栋节点上的函数
function bindApiToBuildings () {
  if (!clickableBuildings.length || (!buildingsApiByKey.size && !buildingsApiByMapName.size)) return  // 3d模型的楼或者接口中的两类索引没数据就跳过

  clickableBuildings.forEach((node) => { // 遍历每个楼节点
    // 先尝试用“楼栋唯一标识符”命中（最稳）
    const raw = node.name || ''  // 3D 模型里的楼名称（如 L1-3 / L2-12 / 善园47#）
    // 规范化标签（去掉空格/全角/大小写）
    const label = normalizeLabel(raw) // 统一“#/空格/全角”

    // ① 直接按 Community_Map_Name 同名命中（最稳定）
    let apiRow = buildingsApiByMapName.get(label) // 按 Community_Map_Name 建立的索引,从这个索引中取数据value
    // ，所以这里apiRow是接口一个楼的全部信息

    // ② 同名没命中：从“佳园17# / 臻园21# / 保利佳园A区17#”里解析出楼号，再回到接口里按【小区名+楼号】匹配
    if (!apiRow) {
      // 拆 “xxx17#” → {prefix:"xxx", no:"17"}
      const m = label.match(/^(.+?)(\d+)\s*#$/)
      if (m) {
        const prefix = normalizeCN(m[1])           // 中文前缀（可能是小区简称/全名）
        const no     = normalizeNo(m[2])


        // 在楼栋列表里找一条：楼号一致，且“中文前缀”能在 小区名 或 楼牌名 里匹配到
        apiRow = buildingList.value.find(it => {
          const n  = normalizeNo(it.Building_Number || it.buildingNumber)
          if (n !== no) return false
          const comm  = normalizeCN(it.Affiliated_Residential_Quart || '')
          const cname = normalizeCN(it.Community_Map_Name || '')
          return comm.includes(prefix) || cname.includes(prefix)
        }) || null
      }
    }

    if (!apiRow) return  // 还是对不上就先跳过

    // 绑定 meta（气泡显示 + 居民按钮取 key 就靠它）
    const meta = { // 组装楼栋元数据,meta是一个对象，包含楼栋的各种信息,meta的中文意思是元数据，meta对象存储了楼栋的各种属性信息
      id: apiRow.Community_Map_Name || raw,
      communityName: normalizeCN(apiRow.Affiliated_Residential_Quart),
      buildingNumber: normalizeNo(apiRow.Building_Number),
      buildingType: apiRow.Building_Type || '居民楼',
      households: apiRow.Current_Number_of_Households || 0,
      floors: apiRow.Number_of_Floors || 0,
      unitsPer: apiRow.Number_of_Units_per_Building || 0,
      manager: apiRow.person_in_charge || '',
      tel: apiRow.Phone_Number || '',
      remark: apiRow.Remarks || ''
    }
    buildingMeta.set(node.name, meta)
  })

  console.log('✅ 已用中文名自动绑定接口：', buildingMeta.size)
}


///////////////////////////////////////////////////////////////////////////////////////////////////

// 在楼顶显示气泡
function showBubble(building) {
  if (currentLabel) {
    currentLabel.element.remove()
    scene.remove(currentLabel)
    currentLabel = null
  }

  // 先从 3D 模型里找中文楼名（如“善园47# / 臻1#”），找不到再用映射表，最后用自身名字
  const labelFromModel = getModelCnLabel(building)
  const labelFromMap   = buildingNameMap?.[building.name]
  const labelToUse     = labelFromModel || labelFromMap || building.name
  //相当于labelToUse是最终要用来显示的楼栋名称，用于后面与接口中的楼栋信息对接

  // 按 Community_Map_Name 去接口索引里查
  const labelStrict = normalizeLabelStrict(labelToUse)
  const apiInfo = apiByMapName.get(labelStrict)
  //其中 apiByMapName 是你从接口 /building/list 获取后通过 rebuildApiIndex() 建立的索引（key = Community_Map_Name）。


  // 组装气泡要展示的字段（命中接口就用接口，没命中继续用旧的 meta 兜底）
  const meta = apiInfo ? {
    id:          apiInfo.Community_Map_Name || labelToUse,              // 气泡标题
    community:   apiInfo.Affiliated_Residential_Quart || apiInfo.Affiliated_Community || '',
    buildingType:apiInfo.Building_Type || '',
    households:  apiInfo.Current_Number_of_Households ?? '',
    floors:      apiInfo.Number_of_Floors ?? '',
    unitsPer:    apiInfo.Number_of_Units_per_Building ?? '',
    manager:     apiInfo.person_in_charge || '',
    tel:         apiInfo.Phone_Number || '',
    remark:      apiInfo.Remarks || '',
    // 你还想展示的字段都可以在这往下加
  } : (buildingMeta.get(building.name) || { id: labelToUse })

  const el = document.createElement('div')  // 气泡的 DOM 元素
  el.className = 'bubble'
  el.style.pointerEvents = 'auto'    // ← 关键：让气泡本身能接收点击
  // 如果你给 labelRenderer.domElement 设过 z-index，这里也可以加一条保险：
  // el.style.zIndex = '10'
  el.innerHTML = `
    <div class="bubble-title">${meta.id ?? '/'}</div>
    <div class="bubble-line">小区：${meta.community ?? '/'}</div>
    <div class="bubble-line">类型：${meta.buildingType ?? '/'}</div>
    <div class="bubble-line">住户：${meta.households ?? '/'}</div>
    <div class="bubble-line">楼层：${meta.floors ?? '/'}</div>
    <div class="bubble-line">单元数：${meta.unitsPer ?? '/'}</div>
    <div class="bubble-line">负责人：${meta.manager ?? '/'}</div>
    <div class="bubble-line">电话：${meta.tel ?? '/'}</div>
    ${meta.remark ? `<div class="bubble-line">备注：${meta.remark}</div>` : ''}
    <!-- 按钮区 -->
    <div style="display:flex;gap:8px;margin:8px 0 6px;">
      <button class="bubble-btn" data-action="residents"
        style="padding:4px 8px;border-radius:6px;border:1px solid rgba(255,255,255,.25);background:#1f6feb;color:#fff;cursor:pointer;">
        居民信息
      </button>
      <button class="bubble-btn" data-action="devices"
        style="padding:4px 8px;border-radius:6px;border:1px solid rgba(255,255,255,.25);background:#2d333b;color:#c9d1d9;cursor:pointer;">
        设备信息
      </button>
    </div>
  `
  // 在你设置 innerHTML 之后，加：是为了让按钮能接收点击
  el.querySelectorAll('.bubble-btn').forEach(btn => {
    btn.style.pointerEvents = 'auto'
  })


  // —— 事件绑定：点击居民信息弹窗
el.querySelector('[data-action="residents"]').addEventListener('click', async (e) => {
  e.stopPropagation();

  // 1) 取模型上的中文楼牌（或兜底名），并解析出 {prefix, no}
  const labelStrict = normalizeLabelStrict(
    getModelCnLabel(building) || building.name || ''
  );
  const parsed = parseModelLabel(labelStrict);

  const title = `${labelStrict || '居民信息'} 居民信息`;
  openModal(title, `<div class="modal-empty">正在加载居民数据...</div>`);

  if (!parsed) {
    modalBox.querySelector('.modal-body').innerHTML =
      `<div class="modal-empty">无法从“${labelStrict}”解析出“中文前缀+楼号”，请检查模型命名。</div>`;
    return;
  }

  const { prefix, no } = parsed;
  console.log('[居民查询-根据模型名]', { labelStrict, prefix, no });

  try {
    // 2) 第一次尝试：把“中文前缀”直接作为后端 communityName 传参（若后端支持模糊/包含，这一步就能命中）
    let rows = await loadResidentsFor(prefix, no); // 过滤也用 prefix

    // 3) 兜底：若后端对 communityName 是精确匹配导致 0 条，再发一次“不过滤 communityName 的请求”
    //    但前端依然按 prefix 严格过滤（通过 opts.filterCommunityName 保证）
    if (!rows.length) {
      console.warn('[居民查询] 按前缀传参无结果，改用空 communityName 请求后再前端过滤。');
      rows = await loadResidentsFor('', no, { filterCommunityName: prefix });
    }

    // 4) 展示
    mountResidentsModal(rows);

    if (!rows.length) {
      modalBox.querySelector('.modal-body').innerHTML =
        `<div class="modal-empty">未找到符合条件的居民：社区包含“${prefix}”，楼号=“${no}”。</div>`;
    }
  } catch (err) {
    modalBox.querySelector('.modal-body').innerHTML =
      `<div class="modal-empty">居民数据加载失败：${err?.message || err}</div>`;
  }
});




  // —— 事件绑定：设备信息（先留个结构，后面接接口就能用）
  el.querySelector('[data-action="devices"]').addEventListener('click', async (e) => {
    e.stopPropagation();
    // 优先用模型里的中文楼名；没有就用 labelToUse
    const cnLabel = normalizeLabelStrict(getModelCnLabel(building) || labelToUse || '');
    openModal(`${cnLabel} 设备信息`, `<div class="modal-empty">正在加载设备数据...</div>`);

    try {
      const rows = await listDevicesByCnLabel(cnLabel); // ← 走统一的“中文楼名 → 设备数组”索引
      const html = renderDevicesTable(rows);
      modalBox.querySelector('.modal-body').innerHTML = html || `<div class="modal-empty">暂无设备数据</div>`;
    } catch (err) {
      modalBox.querySelector('.modal-body').innerHTML =
        `<div class="modal-empty">设备数据加载失败：${err?.message || err}</div>`;
    }
  });


  // 取楼的包围盒顶端作为锚点
  const box = new THREE.Box3().setFromObject(building)
  const pos = box.getCenter(new THREE.Vector3())
  pos.y = box.max.y + 1.5

  currentLabel = new CSS2DObject(el)
  currentLabel.position.copy(pos)
  scene.add(currentLabel)
  markDirty()
}

// 清空选择（点击空白用）
function clearSelection() {
  if (currentHelper) {
    scene.remove(currentHelper)
    currentHelper.geometry.dispose()
    currentHelper.material.dispose()
    currentHelper = null
  }
  if (currentLabel) {
    currentLabel.element.remove()
    scene.remove(currentLabel)
    currentLabel = null
  }
  selectedObjectName.value = ''
  markDirty()
}
////////////////////////////////////////


// 关闭弹窗
function closePopup() {
  selectedObjectName.value = ''
}

// 添加光源
function addLights() {
  // 环境光
  const ambientLight = new THREE.AmbientLight(0xffffff, 2.0)
  scene.add(ambientLight)
  
  // 方向光（模拟太阳光）
  const directionalLight = new THREE.DirectionalLight(0xffffff, 2.5)
  directionalLight.position.set(5, 10, 7.5)
  directionalLight.castShadow = false
  scene.add(directionalLight)
  
  // 柔和的半球光
  const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 2.4)
  scene.add(hemisphereLight)
}

// 添加500*500的地面
function addGround() {
  // 1) 贴图加载（用你刚放到 public/models/.../textures 里的两张图）
  const loader = new THREE.TextureLoader();

  // 颜色贴图（漫反射）
  const colorMap = loader.load(
    // '/models/stone_tiles_02_4k.blend/textures/stone_tiles_02_diff_4k.jpg',
    "/models/worn_tile_floor_4k.blend/textures/worn_tile_floor_diff_4k.jpg"
  );
  colorMap.wrapS = colorMap.wrapT = THREE.RepeatWrapping; // 允许平铺
  colorMap.repeat.set(200, 200); // ✅ 地砖密度：数值越大砖越小，按需调 30~100

  // 用位移贴图当 bump（简单好看）
  const bumpMap = loader.load(
    // '/models/stone_tiles_02_4k.blend/textures/stone_tiles_02_disp_4k.png',
    "models/worn_tile_floor_4k.blend/textures/worn_tile_floor_disp_4k.png"
  );
  bumpMap.wrapS = bumpMap.wrapT = THREE.RepeatWrapping;
  bumpMap.repeat.copy(colorMap.repeat); // 与颜色贴图一致

  // 2) 地面材质
  const groundMaterial = new THREE.MeshStandardMaterial({
    map: colorMap,
    bumpMap: bumpMap,
    bumpScale: 0.08,   // 凹凸强度：0.05~0.15 之间试试
    roughness: 0.95,   // 粗糙一些更自然
    metalness: 0.0
  });

  // 3) 地面网格（把尺寸放大覆盖全场景）
  const groundGeometry = new THREE.PlaneGeometry(1000, 1000); // ← 原来1000太小了
  const ground = new THREE.Mesh(groundGeometry, groundMaterial);
  ground.rotation.x = -Math.PI / 2; // 旋成水平
  ground.position.y = -0.02;        // 稍微压低，避免和道路/建筑闪烁
  ground.receiveShadow = true;

  // 如果之前还有别处再创建了“绿色地面”，记得删/注释避免重复
  scene.add(ground);
}



// ===== 冻结静态网格：不要每帧更新矩阵/阴影 =====
function freezeStatic (root) {
  root.traverse(o => {
    if (!o.isMesh) return
    o.matrixAutoUpdate = false
    o.updateMatrix()
    o.frustumCulled = true
    o.castShadow = false   // 你的灯也不需要阴影就完全关掉，省性能
    o.receiveShadow = false

    const mats = Array.isArray(o.material) ? o.material : [o.material]
    mats.forEach(m => { if (m && m.map) m.map.anisotropy = 2 })
  })
}


// ===== 网格合批为 InstancedMesh（大幅减少 draw calls） =====
function injectBubbleStyle(){
  const style = document.createElement('style')
  style.textContent = `
  .bubble{
    pointer-events:auto;
    min-width:160px; max-width:800px;
    padding:10px 12px; border-radius:12px;
    background:rgba(20,25,35,.92); color:#fff; font-size:12px; line-height:1.4;
    box-shadow:0 6px 18px rgba(0,0,0,.35); backdrop-filter: blur(3px);
  }
  .bubble-title{ font-size:14px; font-weight:600; margin-bottom:6px; }
  .bubble-line{ opacity:.9; }
  .modal-table{ width:100%; border-collapse:collapse; font-size:12px; color:#c9d1d9; }
  .modal-table th, .modal-table td{ padding:8px 10px; border-bottom:1px solid rgba(255,255,255,.06); }
  .modal-table th{ position:sticky; top:0; background:rgba(13,17,23,.9); text-align:left; }
  .modal-table td.phone{ text-align:center; width:64px; }
  .modal-table td.houseNumber{ text-align:center; width:64px; }
  .modal-table td { word-break: break-all; white-space: normal; }
  /* 分组斑马纹：不同门牌深浅交替 */
  .modal-table tr.g0{ background:rgba(255,255,255,.03); }
  .modal-table tr.g1{ background:rgba(255,255,255,.08); }

  .table-meta{ margin:6px 0 8px; opacity:.75; font-size:12px; }
  /* 让弹窗更宽一些 */
  .modal-box{
    width: 80vw;           /* 原来可能是默认宽度，这里直接拉到 90% 视口宽 */
    max-width: 1600px;     /* 上限，按需要调更大 */
    max-height: 80vh;      /* 保证竖向也能滚动 */
  }

  `
  document.head.appendChild(style)
}


//增加“排除函数”参数（避免把楼合批成 InstancedMesh）
function instanceDuplicates (root, isExcludedFn = () => false) {
  const buckets = new Map()
  root.traverse(o => {
    if (!o || !o.isMesh) return
    if (isExcludedFn(o)) return                 // ← 新增：排除“楼”
    if (o.skeleton || o.morphTargetInfluences) return
    const mat = Array.isArray(o.material) ? o.material[0] : o.material
    const key = `${o.geometry?.uuid}|${mat?.uuid}`
    if (!key) return
    if (!buckets.has(key)) buckets.set(key, [])
    buckets.get(key).push(o)
  })

  const m = new THREE.Matrix4()
  buckets.forEach(list => {
    if (list.length < 20) return
    const sample = list[0]
    const inst = new THREE.InstancedMesh(sample.geometry, sample.material, list.length)
    list.forEach((o, i) => {
      o.updateWorldMatrix(true, false)
      m.copy(o.matrixWorld)
      inst.setMatrixAt(i, m)
      o.parent && o.parent.remove(o)
    })
    inst.instanceMatrix.needsUpdate = true
    root.add(inst)
  })
}

// // 判断某节点是不是“楼”（供上面排除用）
// function isBuildingNode(o){
//   return isBuildingName(o?.name)
// }


// 添加网格辅助线
// eslint-disable-next-line no-unused-vars
function addGridHelper() {
  const gridHelper = new THREE.GridHelper(20, 20, 0xcccccc, 0xeeeeee)
  gridHelper.position.y = 0 // 与地面保持同一高度
  scene.add(gridHelper)
  
  // 添加坐标轴辅助
  const axesHelper = new THREE.AxesHelper(10)
  scene.add(axesHelper)
}


// // 动画循环
// let lastVisibilityCheck = 0
// const VISIBILITY_CHECK_INTERVAL = 500 // 每500ms检查一次可见性


// 窗口大小变化处理
function onWindowResize() {
  if (!renderer || !container.value) return
  const w = container.value.clientWidth
  const h = container.value.clientHeight
  camera.aspect = w / h
  camera.updateProjectionMatrix()
  renderer.setSize(w, h)
  if (labelRenderer) labelRenderer.setSize(w, h)
  markDirty() // ← 通知下一帧重绘
}


// 重置视角
function resetView() {
  camera.position.copy(INITIAL_CAMERA_POS)
  controls.target.copy(INITIAL_CAMERA_TARGET)
  camera.lookAt(INITIAL_CAMERA_TARGET)
  controls.update()
  markDirty()
}

// 切换旋转状态
function toggleRotation() {
  isRotating.value = !isRotating.value
  if (controls) {
    controls.autoRotate = isRotating.value
    const baseAnglePerFrame = (2 * Math.PI) / (60 * 60)
    const speed = rotationSpeed > 0 ? (rotationSpeed / baseAnglePerFrame) : 2.0
    controls.autoRotateSpeed = Math.max(0.1, speed)
    markDirty()
  }
}
////////////主程序/////////////////////////////////////////////////////////////////
// 组件挂载时初始化
onMounted(() => {
 // 1) 先把 Three.js 场景跑起来（renderer/canvas/动画循环就绪）
  initScene()

  // 2) 再去请求数据（不 await，不阻塞渲染）
  fetchCommunityData().catch(err => {
    console.error('接口总入口失败：', err)
  })
})
///////////////////////////////////////////////////////////////////////////////////
// 组件卸载时清理资源
onUnmounted(() => {

  isDestroyed = true

  // 1) 停止动画 & 事件
  if (animationId) { cancelAnimationFrame(animationId); animationId = null }
  window.removeEventListener('resize', onWindowResize)
  container.value?.removeEventListener('click', onMouseClick)
  controls?.dispose?.()

  // 2) 先释放外部挂件（云 / 人 / 车）
  // 2) 先释放外部挂件（云 / 人 / 车）
  try { cloudsCtl?.dispose?.() } 
  catch (e) { /* ignore: maybe undefined or disposed */ }

  try { crowdCtl?.dispose?.() } 
  catch (e) { /* ignore */ }

  try { trafficCtl?.dispose?.() } 
  catch (e) { /* ignore */ }
  cloudsCtl = crowdCtl = trafficCtl = null

  // 3) CSS2DRenderer / 气泡
  if (currentLabel) {
    currentLabel.element?.remove?.()
    scene?.remove(currentLabel)
    currentLabel = null
  }
  if (labelRenderer) {
    // CSS2DRenderer 没有 dispose，直接移除 DOM 即可
    labelRenderer.domElement?.parentNode?.removeChild(labelRenderer.domElement)
    labelRenderer = null
  }

  // 4) 关闭弹窗并移除 DOM
  try { closeModal?.() } catch(e){ /* ignore */ }
  if (modalOverlay) {
    modalOverlay.querySelector('.modal-close')?.removeEventListener('click', closeModal)
    modalOverlay.remove()
    modalOverlay = null
    modalBox = null
  }

  // 5) 释放 Three 资源（含纹理）
  const disposeMaterial = (mat) => {
    if (!mat) return
    const maps = ['map','alphaMap','aoMap','bumpMap','displacementMap','emissiveMap','envMap','lightMap','metalnessMap','normalMap','roughnessMap']
    maps.forEach(k => mat[k]?.dispose?.())
    mat.dispose?.()
  }

  if (scene) {
    scene.traverse(obj => {
      if (obj.isMesh || obj.isSprite || obj.isPoints) {
        obj.geometry?.dispose?.()
        if (Array.isArray(obj.material)) obj.material.forEach(disposeMaterial)
        else disposeMaterial(obj.material)
      }
    })
    // 清空场景层级
    while (scene.children.length) scene.remove(scene.children[0])
  }

  // 6) 渲染器
  if (renderer) {
    // 清理 KTX2Loader 缓存
    try { 
      ktx2?.dispose?.() 
    } catch (e) {
      console.warn('KTX2Loader dispose failed:', e)
    }
    ktx2 = null
    gltfLoader = null

    renderer.dispose()
    if (renderer.domElement && renderer.domElement.parentNode) {
      renderer.domElement.parentNode.removeChild(renderer.domElement)
    }
    // 显式丢弃 WebGL 上下文引用
    const extension = renderer.getContext()?.getExtension('WEBGL_lose_context')
    if (extension) extension.loseContext()
    renderer = null
  }

  // 7) 清理引用
  scene = null
  camera = null
  renderer = null
  controls = null
  raycaster = null
  ddModel = null
  modelParts = {}
  visibleParts?.clear?.()

  // 8) （可选）触发手动 GC（仅 DevTools 开启 “Collect garbage” 才有）
  if (typeof window.gc === 'function') {
    try { 
      window.gc() 
    } catch (e) {
      console.warn('Manual GC failed:', e)
    }
  }
})

</script>

<style scoped>
  .dd-model-viewer-container {
    position: relative;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
  }

  .three-container {
    width: 100%;
    height: 100%;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.8);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }

  .loading-spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 15px;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .control-panel {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 10px;
    z-index: 999;
    background-color: rgba(255, 255, 255, 0.9);
    padding: 10px 20px;
    border-radius: 25px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .control-btn {
    padding: 8px 16px;
    background-color: #3498db;
    color: white;
    border: none;
    border-radius: 20px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.3s ease;
  }

  .control-btn:hover {
    background-color: #2980b9;
  }

  /* 对象信息弹窗样式 */
  .object-info-popup {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: rgba(255, 255, 255, 0.95);
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 16px 24px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1001;
    width: 80%;
    max-width: 600px;
    max-height: 80vh;
    overflow-y: auto;
    text-align: center;
    animation: fadeIn 0.3s ease-out;
  }

  .object-info-popup h3 {
    margin-top: 0;
    margin-bottom: 15px;
    text-align: center;
    color: #333;
  }

  .object-info-popup p {
    margin: 0 0 12px 0;
    font-size: 16px;
    color: #333;
  }

  .close-btn {
    margin-top: 15px;
    padding: 10px 20px;
    background-color: #f44336;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: background-color 0.3s ease;
    display: block;
    margin-left: auto;
    margin-right: auto;
  }

  .close-btn:hover {
    background-color: #d32f2f;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, -40%);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%);
    }
  }
/******************居民信息弹窗样式*******************************************************************/
:global(.modal-overlay){ position:fixed; inset:0; background:rgba(0,0,0,.35);
  display:none; align-items:center; justify-content:center; z-index:9999; }
:global(.modal-box){ background:#0d1117; color:#c9d1d9; width:880px; max-height:80vh;
  overflow:auto; border-radius:12px; box-shadow:0 10px 40px rgba(0,0,0,.45);
  border:1px solid rgba(255,255,255,.08); }
:global(.modal-header){ display:flex; align-items:center; justify-content:space-between;
  padding:12px 16px; border-bottom:1px solid rgba(255,255,255,.08);
  position:sticky; top:0; background:#0d1117; z-index:1; }
:global(.modal-title){ font-weight:600; }
:global(.modal-close){ border:none; background:transparent; color:#8b949e; font-size:18px; cursor:pointer; padding:4px 8px; }
:global(.modal-close:hover){ color:#fff; }
:global(.modal-body){ padding:12px 16px 16px; }
:global(.modal-table){ width:100%; border-collapse:collapse; font-size:13px; }
:global(.modal-table th), :global(.modal-table td){ border-bottom:1px solid rgba(255,255,255,.06);
  padding:8px 10px; text-align:left; white-space:nowrap; }
:global(.modal-table thead th){ position:sticky; top:48px; background:#0d1117; }
:global(.modal-empty){ padding:24px; color:#8b949e; text-align:center; }

:global(.resident-filters){ display:flex; flex-wrap:wrap; gap:10px 12px; align-items:flex-end;
  padding:10px 12px; margin:0 0 10px; border:1px solid rgba(255,255,255,.08);
  background:rgba(255,255,255,.02); border-radius:10px; }
:global(.resident-filter-item){ display:flex; gap:8px; align-items:center; }
:global(.resident-filter-label){ color:#8b949e; font-size:12px; min-width:32px; }
:global(.resident-filter-input), :global(.resident-filter-select){
  height:30px; padding:0 10px; border-radius:8px; border:1px solid rgba(255,255,255,.12);
  background:#0b1220; color:#c9d1d9; outline:none; }
:global(.resident-filter-input){ width:120px; }
:global(.resident-filter-select){ width:86px; }
:global(.resident-filter-input:focus), :global(.resident-filter-select:focus){
  border-color: rgba(31,111,235,.7); box-shadow:0 0 0 2px rgba(31,111,235,.15); }
:global(.resident-filter-actions){ margin-left:auto; display:flex; gap:8px; }
:global(.resident-filter-btn){
  height:30px; padding:0 12px; border-radius:8px; border:1px solid rgba(255,255,255,.14);
  background:#161b22; color:#c9d1d9; cursor:pointer; }
:global(.resident-filter-btn:hover){ border-color: rgba(255,255,255,.25); }

:global(.resident-pager){ display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-top:10px; }
:global(.resident-page-list){ display:flex; gap:6px; align-items:center; }
:global(.resident-page-nav), :global(.resident-page-btn){
  height:28px; min-width:28px; padding:0 10px; border-radius:8px;
  border:1px solid rgba(255,255,255,.12); background:#0b1220; color:#c9d1d9; cursor:pointer; }
:global(.resident-page-nav[disabled]){ opacity:.5; cursor:not-allowed; }
:global(.resident-page-btn.is-active){ background:#1f6feb; border-color: rgba(31,111,235,.8); color:#fff; }
:global(.resident-page-info){ color:#8b949e; font-size:12px; margin-left:auto; }

/******************居民信息弹窗样式*******************************************************************/
</style>
